import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useCustomers } from '../../hooks/useCustomers';
import { useProjects } from '../../hooks/useProjects';
import Modal from '../common/Modal';
import SearchableSelect from '../common/SearchableSelect';
import { 
  DocumentChartBarIcon,
  ArrowDownTrayIcon,
  CalendarIcon,
  BuildingOfficeIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  SparklesIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import { 
  generateCustomerMonthlyReport, 
  generateAllCustomersMonthlyReport,
  exportReportAsText,
  exportReportAsCSV,
  exportReportAsPDF,
  CustomerMonthlyReport,
  DateFilterType
} from '../../services/taskReportService';
import { generateAITestReport, generateAIQuickSummary } from '../../services/aiReportService';
import toast from 'react-hot-toast';

interface MonthlyReportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const MonthlyReportModal: React.FC<MonthlyReportModalProps> = ({
  isOpen,
  onClose
}) => {
  const { user } = useAuth();
  const { customers } = useCustomers();
  const { projects } = useProjects();
  
  const [selectedCustomer, setSelectedCustomer] = useState<string>('all');
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);
  const [filterByDate, setFilterByDate] = useState<boolean>(true);
  const [dateFilterType, setDateFilterType] = useState<DateFilterType>('any');
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(['todo', 'in_progress', 'completed', 'cancelled']);
  const [isGenerating, setIsGenerating] = useState(false);
  const [report, setReport] = useState<CustomerMonthlyReport | null>(null);
  const [allReports, setAllReports] = useState<CustomerMonthlyReport[]>([]);
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(new Set());
  const [showTaskSelector, setShowTaskSelector] = useState<boolean>(false);
  const [showColumns, setShowColumns] = useState<'both' | 'estimated' | 'actual'>('actual');
  const [aiGeneratedReport, setAiGeneratedReport] = useState<string>('');
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [showAIReport, setShowAIReport] = useState(false);

  // Év opciók (utolsó 3 év)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 3 }, (_, i) => currentYear - i);

  // Hónap opciók
  const monthOptions = [
    { value: 1, label: 'Január' },
    { value: 2, label: 'Február' },
    { value: 3, label: 'Március' },
    { value: 4, label: 'Április' },
    { value: 5, label: 'Május' },
    { value: 6, label: 'Június' },
    { value: 7, label: 'Július' },
    { value: 8, label: 'Augusztus' },
    { value: 9, label: 'Szeptember' },
    { value: 10, label: 'Október' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  // Ügyfél opciók
  const customerOptions = [
    { value: 'all', label: 'Összes ügyfél', icon: '🏢' },
    ...customers.map(customer => ({
      value: customer.id,
      label: customer.companyName || `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || 'Névtelen',
      icon: '🏢'
    }))
  ];

  const handleGenerate = async () => {
    if (!user?.organizationId) {
      toast.error('Nincs szervezet azonosító');
      return;
    }

    setIsGenerating(true);
    try {
      if (selectedCustomer === 'all') {
        // Összes ügyfél riportja
        const reports = await generateAllCustomersMonthlyReport(
          selectedYear,
          selectedMonth,
          user.organizationId,
          selectedProject || undefined,
          filterByDate,
          selectedStatuses,
          dateFilterType
        );
        setAllReports(reports);
        setReport(null);
        toast.success(`${reports.length} ügyfél riportja elkészült!`);
      } else {
        // Egy ügyfél riportja
        const customer = customers.find(c => c.id === selectedCustomer);
        if (!customer) {
          toast.error('Ügyfél nem található');
          return;
        }

        const customerReport = await generateCustomerMonthlyReport(
          selectedCustomer,
          customer.companyName || `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || 'Névtelen',
          selectedYear,
          selectedMonth,
          user.organizationId,
          selectedProject || undefined,
          filterByDate,
          selectedStatuses,
          dateFilterType
        );
        
        if (customerReport.tasks.length === 0) {
          toast.error('Nincs a szűrési feltételeknek megfelelő feladat!');
          setIsGenerating(false);
          return;
        }
        
        setReport(customerReport);
        setAllReports([]);
        toast.success('Riport elkészült!');
      }
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Hiba történt a riport generálása során');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleExportText = (reportToExport: CustomerMonthlyReport) => {
    const content = exportReportAsText(reportToExport, aiGeneratedReport || undefined);
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `riport_${reportToExport.customerName.replace(/[^a-z0-9]/gi, '_')}_${reportToExport.month}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Riport letöltve (TXT)' + (aiGeneratedReport ? ' - AI reporttal' : ''));
  };

  const handleExportCSV = (reportToExport: CustomerMonthlyReport) => {
    const content = exportReportAsCSV(reportToExport);
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `riport_${reportToExport.customerName.replace(/[^a-z0-9]/gi, '_')}_${reportToExport.month}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Riport letöltve (CSV)');
  };

  const handleExportPDF = (reportToExport: CustomerMonthlyReport) => {
    exportReportAsPDF(reportToExport, showColumns, aiGeneratedReport || undefined);
    toast.success('Riport letöltve (PDF)' + (aiGeneratedReport ? ' - AI reporttal' : ''));
  };

  const handleExportAllAsText = () => {
    allReports.forEach(r => handleExportText(r));
    toast.success(`${allReports.length} riport letöltve!`);
  };

  const handleToggleTask = (taskNumber: string) => {
    const newSelected = new Set(selectedTaskIds);
    if (newSelected.has(taskNumber)) {
      newSelected.delete(taskNumber);
    } else {
      newSelected.add(taskNumber);
    }
    setSelectedTaskIds(newSelected);
  };

  const handleSelectAllTasks = () => {
    if (report) {
      if (selectedTaskIds.size === report.tasks.length) {
        setSelectedTaskIds(new Set());
      } else {
        setSelectedTaskIds(new Set(report.tasks.map(t => t.taskNumber)));
      }
    }
  };

  const getFilteredReport = (originalReport: CustomerMonthlyReport): CustomerMonthlyReport => {
    if (!showTaskSelector || selectedTaskIds.size === 0) {
      return originalReport;
    }

    const filteredTasks = originalReport.tasks.filter(t => selectedTaskIds.has(t.taskNumber));
    const totalEstimatedHours = filteredTasks.reduce((sum, t) => sum + (t.estimatedHours || 0), 0);
    const totalActualHours = filteredTasks.reduce((sum, t) => sum + t.actualHours, 0);
    const completedTasksCount = filteredTasks.filter(t => t.status === 'completed').length;

    return {
      ...originalReport,
      tasks: filteredTasks,
      totalEstimatedHours,
      totalActualHours,
      completedTasksCount,
      totalTasksCount: filteredTasks.length
    };
  };

  const handleClose = () => {
    setReport(null);
    setAllReports([]);
    setSelectedCustomer('all');
    setSelectedTaskIds(new Set());
    setShowTaskSelector(false);
    setAiGeneratedReport('');
    setShowAIReport(false);
    onClose();
  };

  const handleGenerateAIReport = async (reportType: 'full' | 'quick') => {
    if (!report || !user?.organizationId) {
      toast.error('Először generálj egy riportot!');
      return;
    }

    setIsGeneratingAI(true);
    try {
      const filteredReport = getFilteredReport(report);
      
      let aiText = '';
      if (reportType === 'full') {
        aiText = await generateAITestReport(filteredReport, user.organizationId);
        toast.success('AI report elkészült!');
      } else {
        aiText = await generateAIQuickSummary(filteredReport, user.organizationId);
        toast.success('AI összefoglaló elkészült!');
      }
      
      setAiGeneratedReport(aiText);
      setShowAIReport(true);
    } catch (error: any) {
      console.error('AI Report Error:', error);
      toast.error(error.message || 'Hiba az AI report generálása során');
    } finally {
      setIsGeneratingAI(false);
    }
  };

  const renderReportSummary = (reportData: CustomerMonthlyReport) => (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-2 border-blue-200 dark:border-blue-800 rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <BuildingOfficeIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">
            {reportData.customerName}
          </h3>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleExportPDF(getFilteredReport(reportData))}
            className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm flex items-center space-x-1 transition-colors"
            title={showTaskSelector && selectedTaskIds.size > 0 ? `${selectedTaskIds.size} kiválasztott task` : 'Összes task'}
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            <span>PDF</span>
          </button>
          <button
            onClick={() => handleExportText(getFilteredReport(reportData))}
            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm flex items-center space-x-1 transition-colors"
            title={showTaskSelector && selectedTaskIds.size > 0 ? `${selectedTaskIds.size} kiválasztott task` : 'Összes task'}
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            <span>TXT</span>
          </button>
          <button
            onClick={() => handleExportCSV(getFilteredReport(reportData))}
            className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm flex items-center space-x-1 transition-colors"
            title={showTaskSelector && selectedTaskIds.size > 0 ? `${selectedTaskIds.size} kiválasztott task` : 'Összes task'}
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            <span>CSV</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Összes feladat</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{reportData.totalTasksCount}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Befejezett</div>
          <div className="text-2xl font-bold text-green-600 dark:text-green-400">{reportData.completedTasksCount}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Becsült óra</div>
          <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{reportData.totalEstimatedHours.toFixed(1)}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Tényleges óra</div>
          <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{reportData.totalActualHours.toFixed(1)}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Eltérés</div>
          <div className={`text-2xl font-bold ${
            reportData.totalActualHours > reportData.totalEstimatedHours 
              ? 'text-red-600 dark:text-red-400' 
              : 'text-green-600 dark:text-green-400'
          }`}>
            {(reportData.totalActualHours - reportData.totalEstimatedHours).toFixed(1)}
          </div>
        </div>
      </div>

      {/* Feladatok lista */}
      {reportData.tasks.length > 0 && (
        <div className="mt-4">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Feladatok:</h4>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {reportData.tasks.map((task, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700 text-sm"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 dark:text-white">
                      {task.taskNumber} - {task.title}
                    </div>
                    {task.assignedToName && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Felelős: {task.assignedToName}
                      </div>
                    )}
                  </div>
                  <div className="text-right ml-4">
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Becsült: {task.estimatedHours?.toFixed(1) || '0.0'}h
                    </div>
                    <div className="text-xs font-semibold text-purple-600 dark:text-purple-400">
                      Tényleges: {task.actualHours.toFixed(1)}h
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="📊 Havi Kimutatás Generálása"
      size="7xl"
    >
      <div className="space-y-6">
        {/* Beállítások */}
        <div className="bg-gray-50 dark:bg-gray-800/50 border-2 border-gray-200 dark:border-gray-700 rounded-xl p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Ügyfél választó */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <BuildingOfficeIcon className="h-4 w-4 mr-1.5 text-blue-600 dark:text-blue-400" />
                Ügyfél
              </label>
              <SearchableSelect
                options={customerOptions}
                value={selectedCustomer}
                onChange={setSelectedCustomer}
                placeholder="Válassz ügyfelet..."
              />
            </div>

            {/* Projekt választó */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <BuildingOfficeIcon className="h-4 w-4 mr-1.5 text-indigo-600 dark:text-indigo-400" />
                Projekt (opcionális)
              </label>
              <select
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                className="w-full px-4 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:border-blue-500 dark:focus:border-blue-400 focus:ring-4 focus:ring-blue-500/20 transition-all"
              >
                <option value="">Összes projekt</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>{project.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Év választó */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <CalendarIcon className="h-4 w-4 mr-1.5 text-green-600 dark:text-green-400" />
                Év
              </label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="w-full px-4 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:border-blue-500 dark:focus:border-blue-400 focus:ring-4 focus:ring-blue-500/20 transition-all"
              >
                {yearOptions.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            {/* Hónap választó */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <CalendarIcon className="h-4 w-4 mr-1.5 text-purple-600 dark:text-purple-400" />
                Hónap
              </label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="w-full px-4 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:border-blue-500 dark:focus:border-blue-400 focus:ring-4 focus:ring-blue-500/20 transition-all"
              >
                {monthOptions.map(month => (
                  <option key={month.value} value={month.value}>{month.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Dátum szűrő checkbox */}
          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg space-y-3">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="filterByDate"
                checked={filterByDate}
                onChange={(e) => setFilterByDate(e.target.checked)}
                className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="filterByDate" className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                Csak a kiválasztott hónapban aktív task-ok (ha kikapcsolod, minden task benne lesz)
              </label>
            </div>
            
            {/* Dátum típus választó - csak ha a szűrés be van kapcsolva */}
            {filterByDate && (
              <div>
                <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <ClockIcon className="h-4 w-4 mr-1.5 text-blue-600 dark:text-blue-400" />
                  Melyik dátum alapján szűrjön?
                </label>
                <select
                  value={dateFilterType}
                  onChange={(e) => setDateFilterType(e.target.value as DateFilterType)}
                  className="w-full px-4 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:border-blue-500 dark:focus:border-blue-400 focus:ring-4 focus:ring-blue-500/20 transition-all"
                >
                  <option value="any">Bármelyik dátum (létrehozás, befejezés, határidő vagy frissítés)</option>
                  <option value="createdAt">Létrehozás dátuma</option>
                  <option value="completedDate">Befejezés dátuma</option>
                  <option value="dueDate">Határidő</option>
                  <option value="updatedAt">Utolsó frissítés dátuma</option>
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
                  {dateFilterType === 'any' && '📅 A feladat bármelyik dátuma a kiválasztott hónapba esik'}
                  {dateFilterType === 'createdAt' && '📝 Csak a hónapban létrehozott feladatok'}
                  {dateFilterType === 'completedDate' && '✅ Csak a hónapban befejezett feladatok'}
                  {dateFilterType === 'dueDate' && '⏰ Csak a hónapban esedékes feladatok'}
                  {dateFilterType === 'updatedAt' && '🔄 Csak a hónapban frissített feladatok'}
                </p>
              </div>
            )}
          </div>

          {/* Státusz szűrő */}
          <div className="mt-4 p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-lg">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
              Mely státuszú feladatok kerüljenek a riportba?
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedStatuses.includes('todo')}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedStatuses([...selectedStatuses, 'todo']);
                    } else {
                      setSelectedStatuses(selectedStatuses.filter(s => s !== 'todo'));
                    }
                  }}
                  className="rounded border-gray-300 dark:border-gray-600 text-purple-600 focus:ring-purple-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Tennivaló</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedStatuses.includes('in_progress')}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedStatuses([...selectedStatuses, 'in_progress']);
                    } else {
                      setSelectedStatuses(selectedStatuses.filter(s => s !== 'in_progress'));
                    }
                  }}
                  className="rounded border-gray-300 dark:border-gray-600 text-purple-600 focus:ring-purple-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Folyamatban</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedStatuses.includes('completed')}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedStatuses([...selectedStatuses, 'completed']);
                    } else {
                      setSelectedStatuses(selectedStatuses.filter(s => s !== 'completed'));
                    }
                  }}
                  className="rounded border-gray-300 dark:border-gray-600 text-purple-600 focus:ring-purple-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Befejezett</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedStatuses.includes('cancelled')}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedStatuses([...selectedStatuses, 'cancelled']);
                    } else {
                      setSelectedStatuses(selectedStatuses.filter(s => s !== 'cancelled'));
                    }
                  }}
                  className="rounded border-gray-300 dark:border-gray-600 text-purple-600 focus:ring-purple-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Törölve</span>
              </label>
            </div>
          </div>

          {/* Oszlop választó */}
          <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
              Mely óraszámok jelenjenek meg a riportban?
            </label>
            <div className="flex items-center space-x-4">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="showColumns"
                  value="actual"
                  checked={showColumns === 'actual'}
                  onChange={(e) => setShowColumns(e.target.value as any)}
                  className="text-green-600 focus:ring-green-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Csak tényleges</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="showColumns"
                  value="estimated"
                  checked={showColumns === 'estimated'}
                  onChange={(e) => setShowColumns(e.target.value as any)}
                  className="text-green-600 focus:ring-green-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Csak becsült</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="showColumns"
                  value="both"
                  checked={showColumns === 'both'}
                  onChange={(e) => setShowColumns(e.target.value as any)}
                  className="text-green-600 focus:ring-green-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Mindkettő</span>
              </label>
            </div>
          </div>

          {/* Generálás gomb */}
          <div className="mt-4 space-y-3">
            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
              <p className="text-xs text-amber-700 dark:text-amber-400 flex items-center">
                <ExclamationCircleIcon className="h-4 w-4 mr-1.5 flex-shrink-0" />
                A generálás után láthatod az előnézetet a feladatok listájával és összesítéssel, mielőtt letöltenéd PDF-ben.
              </p>
            </div>
            <button
              onClick={handleGenerate}
              disabled={isGenerating}
              className="w-full px-5 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              <DocumentChartBarIcon className="h-5 w-5" />
              <span>{isGenerating ? 'Generálás...' : 'Riport Generálása'}</span>
            </button>
          </div>
        </div>

        {/* Eredmények */}
        {report && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Riport Eredménye
              </h3>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleGenerateAIReport('quick')}
                  disabled={isGeneratingAI}
                  className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-lg text-sm transition-colors disabled:opacity-50 flex items-center space-x-2"
                  title="Rövid AI összefoglaló (3-5 mondat)"
                >
                  <SparklesIcon className="h-4 w-4" />
                  <span>{isGeneratingAI ? 'Generálás...' : 'Gyors AI'}</span>
                </button>
                <button
                  onClick={() => handleGenerateAIReport('full')}
                  disabled={isGeneratingAI}
                  className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg text-sm transition-colors disabled:opacity-50 flex items-center space-x-2"
                  title="Részletes AI tesztelői report"
                >
                  <SparklesIcon className="h-4 w-4" />
                  <span>{isGeneratingAI ? 'Generálás...' : 'Teljes AI Report'}</span>
                </button>
                <button
                  onClick={() => {
                    setShowTaskSelector(!showTaskSelector);
                    if (!showTaskSelector) {
                      setSelectedTaskIds(new Set(report.tasks.map(t => t.taskNumber)));
                    }
                  }}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm transition-colors"
                >
                  {showTaskSelector ? '✓ Task szűrő aktív' : 'Task szűrő'}
                </button>
              </div>
            </div>

            {/* Task kiválasztó */}
            {showTaskSelector && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-200 dark:border-yellow-700 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                    Válaszd ki, mely task-ok kerüljenek a riportba ({selectedTaskIds.size}/{report.tasks.length})
                  </h4>
                  <button
                    onClick={handleSelectAllTasks}
                    className="px-3 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded text-xs transition-colors"
                  >
                    {selectedTaskIds.size === report.tasks.length ? 'Összes kijelölés törlése' : 'Összes kijelölése'}
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-64 overflow-y-auto">
                  {report.tasks.map(task => (
                    <label
                      key={task.taskNumber}
                      className="flex items-start space-x-2 p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedTaskIds.has(task.taskNumber)}
                        onChange={() => handleToggleTask(task.taskNumber)}
                        className="mt-1 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-mono text-gray-600 dark:text-gray-400">{task.taskNumber}</div>
                        <div className="text-sm text-gray-900 dark:text-white truncate">{task.title}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {task.actualHours.toFixed(1)}h
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* AI-generált Report */}
            {showAIReport && aiGeneratedReport && (
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-2 border-purple-200 dark:border-purple-700 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <SparklesIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                    <h4 className="text-lg font-bold text-gray-900 dark:text-white">
                      AI-generált Tesztelői Report
                    </h4>
                  </div>
                  <div className="flex items-center space-x-2">
                    <PencilIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">Szerkeszthető</span>
                  </div>
                </div>
                
                <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border-2 border-purple-200 dark:border-purple-600">
                  <textarea
                    value={aiGeneratedReport}
                    onChange={(e) => setAiGeneratedReport(e.target.value)}
                    className="w-full h-96 px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:border-purple-500 dark:focus:border-purple-400 focus:ring-4 focus:ring-purple-500/20 transition-all resize-none font-mono text-sm"
                    placeholder="Az AI által generált report itt jelenik meg. Szabadon szerkesztheted..."
                  />
                </div>

                <div className="mt-4 flex items-center justify-between">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    💡 Tipp: Az AI által generált szöveget szabadon szerkesztheted, majd exportálhatod.
                  </p>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(aiGeneratedReport);
                        toast.success('Vágólapra másolva!');
                      }}
                      className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm transition-colors"
                    >
                      📋 Másolás
                    </button>
                    <button
                      onClick={() => {
                        const blob = new Blob([aiGeneratedReport], { type: 'text/markdown;charset=utf-8' });
                        const url = URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = `ai_report_${report.customerName.replace(/[^a-z0-9]/gi, '_')}_${report.month}.md`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        URL.revokeObjectURL(url);
                        toast.success('AI report letöltve (Markdown)');
                      }}
                      className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-colors"
                    >
                      💾 Letöltés (MD)
                    </button>
                    <button
                      onClick={() => setShowAIReport(false)}
                      className="px-3 py-1.5 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-sm transition-colors"
                    >
                      ✕ Bezárás
                    </button>
                  </div>
                </div>
              </div>
            )}

            {renderReportSummary(getFilteredReport(report))}
          </div>
        )}

        {allReports.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Összes Ügyfél Riportja ({allReports.length} db)
              </h3>
              <button
                onClick={handleExportAllAsText}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm flex items-center space-x-2 transition-colors"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                <span>Összes Letöltése (TXT)</span>
              </button>
            </div>
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {allReports.map((r, index) => (
                <div key={index}>
                  {renderReportSummary(r)}
                </div>
              ))}
            </div>
          </div>
        )}

        {!report && allReports.length === 0 && !isGenerating && (
          <div className="text-center py-12">
            <DocumentChartBarIcon className="h-16 w-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">
              Válassz ügyfelet, évet és hónapot, majd kattints a Riport Generálása gombra
            </p>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end space-x-3 pt-6 mt-6 border-t-2 border-gray-100 dark:border-gray-700">
        <button
          onClick={handleClose}
          className="px-5 py-2.5 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl font-medium transition-colors"
        >
          Bezárás
        </button>
      </div>
    </Modal>
  );
};

export default MonthlyReportModal;
